import axios from 'axios';

// Automatically detect environment and set appropriate URL
const getApiUrl = () => {
  // Check if we're in development mode
  if (process.env.NODE_ENV === 'development' || 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5002';
  }

  if (window.location.hostname === 'au.bookedsolid.health') {
    return 'https://au.api.bookedsolid.health';
  }

  if (window.location.hostname === 'dashboard.bookedsolid.co.uk') {
    return 'https://v2.api.bookedsolid.co.uk';
  }

  // Production URL
  return "https://v2.api.bookedsolid.health";
};

const url = getApiUrl();

/**
 * Basic JWT validation utilities
 */
class JWTValidator {
  /**
   * Decode JWT payload without verification (client-side basic check only)
   * @param {string} token 
   * @returns {Object|null} Decoded payload or null if invalid
   */
  static decodePayload(token) {
    if (!token || typeof token !== 'string') return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

      return JSON.parse(decoded);
    } catch (error) {
      console.warn('JWT decode error:', error);
      return null;
    }
  }

  /**
   * Basic client-side JWT validation checks
   * Note: This is NOT cryptographically secure - server validation is still required
   * @param {string} token 
   * @returns {Object} Validation result with details
   */
  static validateBasicStructure(token) {
    if (!token) {
      return { isValid: false, reason: 'No token provided' };
    }

    const payload = this.decodePayload(token);
    if (!payload) {
      return { isValid: false, reason: 'Invalid token structure' };
    }

    // Check expiration
    if (payload.exp) {
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        return { isValid: false, reason: 'Token expired', expired: true };
      }
    }

    // Check issued at time (not in future)
    if (payload.iat) {
      const now = Math.floor(Date.now() / 1000);
      if (payload.iat > now + 60) { // Allow 60 second clock skew
        return { isValid: false, reason: 'Token issued in future' };
      }
    }

    // Check required claims exist
    if (!payload.sub && !payload.user_id && !payload.email) {
      return { isValid: false, reason: 'Missing user identifier' };
    }

    return {
      isValid: true,
      payload,
      expiresAt: payload.exp ? new Date(payload.exp * 1000) : null,
      issuedAt: payload.iat ? new Date(payload.iat * 1000) : null
    };
  }
}

/**
 * Authentication utility class for handling JWT token validation and management
 */
class AuthService {
  constructor() {
    this.tokenCheckInProgress = false;
    this.tokenValidationPromise = null;
  }

  /**
   * Check if token is expired or will expire soon (client-side check)
   * @param {number} bufferMinutes - Minutes before expiry to consider "soon to expire"
   * @returns {Object} Token expiry status
   */
  checkTokenExpiry(bufferMinutes = 5) {
    // Token is an httpOnly cookie — not accessible from JS.
    // Use client_id presence as a proxy for "logged in".
    const loggedIn = !!localStorage.getItem('client_id');
    return { hasToken: loggedIn, expired: !loggedIn, soonToExpire: false };
  }

  /**
   * Get the current admin session token
   * @returns {string|null} The token or null if not found
   */
  getToken() {
    // Token is now an httpOnly cookie — not readable from JS.
    // Returns client_id as a proxy for "are we logged in" checks.
    return localStorage.getItem('client_id') || null;
  }

  /**
   * Get ISV session token
   * @returns {string|null} The ISV token or null if not found
   */
  getISVToken() {
    // ISV token is now an httpOnly cookie — not readable from JS.
    return null;
  }

  /**
   * Remove all authentication data from storage
   */
  clearAuth() {
    // Clear specific auth-related keys instead of everything
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('operator');
    localStorage.removeItem('admin');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('client_id');
    localStorage.removeItem('isv_id');
    localStorage.removeItem('registrationDate');

    sessionStorage.removeItem('isAdmin');

    // Clear any cached user data
    this.cachedUserData = null;
    this.lastValidationTime = null;
  }

  /**
   * Check if user appears to be logged in (has token in storage)
   * This is just a preliminary check - use validateToken() for security
   * @returns {boolean}
   */
  hasToken() {
    return !!(this.getToken() || this.getISVToken());
  }

  /**
   * Validate token with the backend server
   * @param {string} token - The JWT token to validate
   * @returns {Promise<Object>} Validation result with user data or error
   */
  async validateTokenWithServer() {
    try {
      // Make a request to a protected endpoint — the httpOnly cookie is sent automatically.
      const response = await axios.get(`${url}/login/validate-token`, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000 // 10 second timeout
      });

      if (response.status === 200 && response.data.success) {
        return {
          isValid: true,
          user: response.data.user,
          tokenInfo: response.data.tokenInfo,
          clientSideValidation: true,
        };
      } else {
        return { isValid: false, error: 'Token validation failed' };
      }
    } catch (error) {
      console.error('Token validation error:', error);

      if (error.response?.status === 401) {
        return { isValid: false, error: 'Token expired or invalid', expired: true };
      } else if (error.response?.status === 403) {
        return { isValid: false, error: 'Access forbidden', forbidden: true };
      } else if (error.code === 'ECONNABORTED') {
        return { isValid: false, error: 'Token validation timeout', timeout: true };
      } else {
        return { isValid: false, error: 'Network error during token validation', networkError: true };
      }
    }
  }

  /**
   * Validate the current user's authentication status
   * Uses caching to avoid excessive server requests
   * @param {boolean} forceRefresh - Force a fresh validation from server
   * @returns {Promise<Object>} Validation result
   */
  async validateAuth(forceRefresh = false) {
    // If a validation is already in progress, return that promise
    if (this.tokenCheckInProgress && !forceRefresh) {
      return this.tokenValidationPromise;
    }

    // Check cache first (valid for 5 minutes)
    const now = Date.now();
    if (!forceRefresh && this.lastValidationTime && (now - this.lastValidationTime) < 300000) {
      if (this.cachedUserData) {
        return { isValid: true, user: this.cachedUserData, cached: true };
      }
    }

    // Mark validation as in progress
    this.tokenCheckInProgress = true;

    this.tokenValidationPromise = this._performValidation();

    try {
      const result = await this.tokenValidationPromise;
      return result;
    } finally {
      this.tokenCheckInProgress = false;
      this.tokenValidationPromise = null;
    }
  }

  /**
   * Internal method to perform the actual validation
   * @private
   */
  async _performValidation() {
    // Tokens are httpOnly cookies — sent automatically by the browser.
    // If there's no client_id we know the user hasn't logged in, skip the network call.
    if (!localStorage.getItem('client_id')) {
      return { isValid: false, error: 'No authentication token found' };
    }

    let result = await this.validateTokenWithServer();

    if (result.isValid) {
      // Cache the successful validation
      this.cachedUserData = result.user;
      this.lastValidationTime = Date.now();

      // Update localStorage with fresh user data
      if (result.user) {
        localStorage.setItem('name', result.user.name || '');
        localStorage.setItem('email', result.user.email || '');
        if (result.user.client_id) {
          localStorage.setItem('client_id', result.user.client_id);
        }
      }
    }

    return result;
  }

  /**
   * Generate device fingerprint for 2FA device recognition
   * @returns {string} Device fingerprint
   */
  generateDeviceFingerprint() {
    // Create a basic device fingerprint using available browser information
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);

    // Generate a session-specific component to make each browser tab/window unique
    let sessionId = sessionStorage.getItem('bs_session_id');
    if (!sessionId) {
      sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
      sessionStorage.setItem('bs_session_id', sessionId);
    }

    const fingerprint = btoa(JSON.stringify({
      userAgent: navigator.userAgent.substring(0, 200), // Limit size
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL().substring(0, 100), // Limit canvas data
      sessionId: sessionId // Make each session unique
    }));

    return fingerprint;
  }

  /**
   * Login with credentials and store the resulting token
   * @param {string} email 
   * @param {string} password 
   * @param {string} captchaToken - reCAPTCHA token (optional for now, required when CAPTCHA is enabled)
   * @param {string} trustToken - Device trust token for bypassing 2FA
   * @returns {Promise<Object>} Login result
   */
  async login(email, password, captchaToken = null, trustToken = null) {
    try {
      const storedTrustToken = localStorage.getItem('device_trust_token');

      const loginData = {
        email,
        password,
        trustToken: trustToken || storedTrustToken
      };

      // Include CAPTCHA token if provided
      if (captchaToken) {
        loginData.captchaToken = captchaToken;
      }

      const response = await axios.post(`${url}/login`, loginData);

      // Handle 2FA required response
      if (response.status === 200 && response.data.requiresTwoFA) {
        return {
          success: false,
          requiresTwoFA: true,
          userId: response.data.userId,
          userType: response.data.userType,
          email: response.data.email,
          deviceFingerprint: response.data.deviceFingerprint,
          challengeToken: response.data.challengeToken,
          loginData: { email, password }, // Store for later use
          message: 'Two-factor authentication required'
        };
      }

      // Handle successful login
      if (response.status === 200 && response.data.success) {
        // Store non-sensitive authentication data
        localStorage.setItem('name', response.data.name || '');
        localStorage.setItem('email', response.data.email || '');
        localStorage.setItem('registrationDate', response.data.registrationDate || '');

        if (response.data.isv) {
          localStorage.setItem('isv_id', response.data.isv._id);
          localStorage.setItem('client_id', response.data.isv.subaccounts[0]._id);
          localStorage.setItem('isAdmin', 'true');
        } else {
          localStorage.setItem('client_id', response.data.client_id);
          localStorage.setItem('isAdmin', response.data.isAdmin || 'false');
          localStorage.removeItem('isv_id');
        }

        // Clear any cached data
        this.cachedUserData = null;
        this.lastValidationTime = null;

        window.dispatchEvent(new Event('settings_updated'));
        window.dispatchEvent(new Event('auth_updated'));

        return { success: true, user: response.data };
      } else {
        return { success: false, error: 'Login failed - invalid response' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  }

  /**
   * Logout the current user and clear all authentication data
   * @returns {Promise<void>}
   */
  async logout() {
    // The server identifies the user via the httpOnly cookie — send withCredentials so the
    // cookie is included.  This invalidates the token in the DB and clears both cookies.
    try {
      await axios.post(`${url}/login/logout`, {}, {
        withCredentials: true,
        timeout: 5000
      });
    } catch (error) {
      // Logout errors are not critical — clear local state regardless
      console.warn('Logout notification failed:', error);
    }

    // Clear all non-sensitive authentication data from localStorage
    this.clearAuth();
  }

  /**
   * Check if the validation cache is still valid (within 5 minutes)
   * @returns {boolean}
   */
  isValidationCacheValid() {
    if (!this.lastValidationTime) return false;
    const now = Date.now();
    return (now - this.lastValidationTime) < 300000; // 5 minutes
  }

  /**
   * Check if the current user has admin privileges
   * @returns {boolean}
   */
  isAdmin() {
    // Return cached admin status if available and recently validated
    if (this.cachedUserData && this.isValidationCacheValid()) {
      return this.cachedUserData.isAdmin || false;
    }

    // Fallback to localStorage for immediate response, but this should be validated with validateAuth()
    return localStorage.getItem('isAdmin') === 'true';
  }

  /**
   * Get current user information from localStorage
   * Note: This should be validated with validateAuth() for security
   * @returns {Object|null}
   */
  getCurrentUser() {
    if (!this.hasToken()) return null;

    return {
      name: localStorage.getItem('name'),
      email: localStorage.getItem('email'),
      client_id: localStorage.getItem('client_id'),
      isAdmin: this.isAdmin(),
      registrationDate: localStorage.getItem('registrationDate')
    };
  }

  /**
   * Security monitoring: Check for potential tampering
   * @returns {Array} Array of security warnings
   */
  getSecurityWarnings() {
    const warnings = [];
    // Token is an httpOnly cookie — cannot be inspected from JS.
    // Client-side JWT checks are no longer possible; server-side validateAuth() is the source of truth.
    return warnings;
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
